import { useDashboardMetrics } from '../hooks/use-dashboard-metrics';
import { MetricCard } from '@/components/organisms/cards';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Skeleton } from '@/components/atoms/skeleton';
import { Progress } from '@/components/atoms/progress';
import { Badge } from '@/components/atoms/badge';
import {
  DollarSign,
  ShoppingCart,
  Package,
  TrendingUp,
  Users,
  Search,
  Eye,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import { formatPrice, convertFromEur } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { useTranslation } from 'react-i18next';

export function DashboardContainer() {
  const { t } = useTranslation();
  const { currency } = useConfigStore();
  const { data, isLoading, error } = useDashboardMetrics();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="gap-4 py-4">
              <div className="px-4 space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-32" />
              </div>
            </Card>
          ))}
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('dashboard.error')}</p>
      </div>
    );
  }

  if (!data) return null;

  const { metrics, ordersData, productSearches, productsData, summary, recentOrders } = data;

  // Status translation map for dynamic keys
  const statusTranslations: Record<string, string> = {
    pending: t('orders.status.pending'),
    confirmed: t('orders.status.confirmed'),
    processing: t('orders.status.processing'),
    shipped: t('orders.status.shipped'),
    delivered: t('orders.status.delivered'),
    cancelled: t('orders.status.cancelled'),
    refunded: t('orders.status.refunded'),
    in_progress: t('orders.status.in_progress'),
    waiting_validation: t('orders.status.waiting_validation'),
    canceled_by_system: t('orders.status.canceled_by_system'),
    canceled_by_seller: t('orders.status.canceled_by_seller'),
    canceled_by_buyer: t('orders.status.canceled_by_buyer'),
    payment_failed: t('orders.status.payment_failed'),
    payment_pending: t('orders.status.payment_pending'),
    completed: t('orders.status.completed'),
  };

  // Format status labels with translation
  const formatStatus = (status: string) => {
    return statusTranslations[status] || status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Get status color
  const getStatusColor = (status: string) => {
    if (status.includes('canceled') || status.includes('failed')) return 'destructive';
    if (status.includes('completed') || status.includes('delivered')) return 'default';
    if (status.includes('pending') || status.includes('waiting')) return 'secondary';
    return 'outline';
  };

  return (
    <div className="space-y-6">
      {/* Main Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title={t('dashboard.totalOrders')}
          value={metrics.totalOrders.toString()}
          icon={ShoppingCart}
          trend={{ value: metrics.ordersTrend, label: t('dashboard.vsLastPeriod') }}
        />
        <MetricCard
          title={t('dashboard.totalRevenue')}
          value={formatPrice(convertFromEur(metrics.totalRevenue, currency), currency)}
          icon={DollarSign}
          trend={{ value: metrics.revenueTrend, label: t('dashboard.vsLastPeriod') }}
        />
        <MetricCard
          title={t('dashboard.pendingOrders')}
          value={metrics.pendingOrders.toString()}
          icon={Clock}
        />
        <MetricCard
          title={t('dashboard.todaysSales')}
          value={formatPrice(convertFromEur(metrics.todaysSales, currency), currency)}
          icon={TrendingUp}
          trend={{ value: metrics.salesTrend, label: t('dashboard.vsLastPeriod') }}
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
                <Eye className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('dashboard.shopVisits')}</p>
                <p className="text-2xl font-bold">{data.shopVisits.totalVisits}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-full">
                <Search className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('dashboard.searchAppearances')}</p>
                <p className="text-2xl font-bold">{productSearches.totalSearchAppearances}</p>
                {productSearches.growthPercentage > 0 && (
                  <p className="text-xs text-green-600">+{productSearches.growthPercentage.toFixed(0)}%</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900 rounded-full">
                <Package className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('dashboard.activeProducts')}</p>
                <p className="text-2xl font-bold">{productsData.activeProducts}/{productsData.totalProducts}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-full">
                <Users className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('dashboard.conversionRate')}</p>
                <p className="text-2xl font-bold">{summary.conversionRate.toFixed(1)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Score & Orders Stats */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Performance Score */}
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.performanceScore')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-center">
                <div className="relative w-32 h-32">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      className="text-muted"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={`${(summary.performanceScore / 100) * 352} 352`}
                      className="text-primary"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold">{summary.performanceScore}</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span>{t('dashboard.completionRate')}: {ordersData.completionRate.toFixed(1)}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  <span>{t('dashboard.cancellationRate')}: {ordersData.cancellationRate.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders by Status */}
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.ordersByStatus')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(ordersData.ordersByStatus).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant={getStatusColor(status)}>
                      {formatStatus(status)}
                    </Badge>
                  </div>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
              {Object.keys(ordersData.ordersByStatus).length === 0 && (
                <p className="text-muted-foreground text-sm">{t('dashboard.noData')}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Orders by Country */}
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.ordersByCountry')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(ordersData.ordersByCountry).map(([country, count]) => {
                const total = Object.values(ordersData.ordersByCountry).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? (count / total) * 100 : 0;
                return (
                  <div key={country} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{country}</span>
                      <span>{count} ({percentage.toFixed(0)}%)</span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                );
              })}
              {Object.keys(ordersData.ordersByCountry).length === 0 && (
                <p className="text-muted-foreground text-sm">{t('dashboard.noData')}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search Terms & Products */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Top Search Terms */}
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.topSearchTerms')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {productSearches.topSearchTerms.slice(0, 8).map((term, index) => (
                <div key={term.term} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground text-sm w-6">#{index + 1}</span>
                    <span className="font-medium">{term.term}</span>
                  </div>
                  <Badge variant="secondary">{term.count}</Badge>
                </div>
              ))}
              {productSearches.topSearchTerms.length === 0 && (
                <p className="text-muted-foreground text-sm">{t('dashboard.noData')}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Product Alerts */}
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.productAlerts')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {productsData.outOfStockProducts > 0 && (
                <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-950 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="font-medium text-red-700 dark:text-red-400">{t('dashboard.outOfStock')}</p>
                    <p className="text-sm text-red-600 dark:text-red-500">
                      {productsData.outOfStockProducts} {t('dashboard.productsNeedAttention')}
                    </p>
                  </div>
                </div>
              )}
              {productsData.lowStockProducts > 0 && (
                <div className="flex items-center gap-3 p-3 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  <div>
                    <p className="font-medium text-yellow-700 dark:text-yellow-400">{t('dashboard.lowStock')}</p>
                    <p className="text-sm text-yellow-600 dark:text-yellow-500">
                      {productsData.lowStockProducts} {t('dashboard.productsNeedAttention')}
                    </p>
                  </div>
                </div>
              )}
              {productsData.outOfStockProducts === 0 && productsData.lowStockProducts === 0 && (
                <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="font-medium text-green-700 dark:text-green-400">{t('dashboard.allProductsOk')}</p>
                    <p className="text-sm text-green-600 dark:text-green-500">
                      {t('dashboard.noStockIssues')}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold">{productsData.totalProducts}</p>
                  <p className="text-sm text-muted-foreground">{t('dashboard.totalProducts')}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{productsData.activeProducts}</p>
                  <p className="text-sm text-muted-foreground">{t('dashboard.activeProducts')}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Orders */}
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.recentOrders')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentOrders?.length > 0 ? (
              recentOrders.map((order, index) => (
                <div
                  key={order.id || `order-${index}`}
                  className="flex items-center justify-between border-b pb-3 last:border-0 last:pb-0"
                >
                  <div>
                    <p className="font-medium">#{order.refId}</p>
                    <p className="text-sm text-muted-foreground">{order.customerName}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{formatPrice(convertFromEur(order.total, currency), currency)}</p>
                    <Badge variant={getStatusColor(order.status)} className="mt-1">
                      {formatStatus(order.status)}
                    </Badge>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-sm text-center py-4">{t('dashboard.noRecentOrders')}</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
